import { useTranslation } from "react-i18next";
import Box from "../Box";
import EmptyState from "../EmptyView";
import ScrollContainer from "../ScrollContainer";
import { AllLessonsSection } from "./AllLessonsSection";
import { CompletedLessonsSection } from "./CompletedLessonsSection";
import { ContinueLearningSection } from "./ContinueLearningSection";
import { SuggestedForYouSection } from "./SuggestedForYouSection";
import type {
	CompletedLesson,
	ContinueLearningLesson,
	Lesson,
	SuggestedLesson,
} from "./types";

type Props = {
	continueLearningLessons: ContinueLearningLesson[];
	suggestedLessons: SuggestedLesson[];
	completedLessons: CompletedLesson[];
	allLessons: Lesson[];
	allLessonsTitle?: string;
	onResumePress: (lesson: ContinueLearningLesson) => void;
	onSuggestedLessonPress: (lesson: SuggestedLesson) => void;
	onCompletedLessonPress: (lesson: CompletedLesson) => void;
	onLessonPress: (lesson: Lesson) => void;
	onViewAllPress: () => void;
};

export const LessonsHomeScreen = ({
	continueLearningLessons,
	suggestedLessons,
	completedLessons,
	allLessons,
	allLessonsTitle,
	onResumePress,
	onSuggestedLessonPress,
	onCompletedLessonPress,
	onLessonPress,
	onViewAllPress,
}: Props) => {
	const { t } = useTranslation();
	const hasLessons =
		continueLearningLessons.length > 0 ||
		suggestedLessons.length > 0 ||
		completedLessons.length > 0 ||
		allLessons.length > 0;

	return (
		<Box flex={1} height="100%">
			<ScrollContainer
				contentContainerStyle={{
					paddingBottom: 96,
					paddingTop: 16,
				}}
				maxWidth="sm"
			>
				{hasLessons ? (
					<Box gap={24} paddingBottom={24}>
						<ContinueLearningSection
							lessons={continueLearningLessons}
							onResumePress={onResumePress}
						/>
						<SuggestedForYouSection
							lessons={suggestedLessons}
							onLessonPress={onSuggestedLessonPress}
						/>
						<CompletedLessonsSection
							lessons={completedLessons}
							onLessonPress={onCompletedLessonPress}
						/>
						<AllLessonsSection
							title={allLessonsTitle}
							lessons={allLessons}
							onLessonPress={onLessonPress}
							onViewAllPress={onViewAllPress}
						/>
					</Box>
				) : (
					<Box flex={1} minHeight={480} paddingTop={24}>
						<EmptyState
							title={t("lessons.home.emptyState.title")}
							subtitle={t("lessons.home.emptyState.subtitle")}
						/>
					</Box>
				)}
			</ScrollContainer>
		</Box>
	);
};
